import React from 'react';
import { Dialog } from 'primereact/dialog';
import { CircleCheck, Loader2, Trash2, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

interface ConfirmDeleteModalProps {
  visible: boolean;
  loading?: boolean;
  /** Short label of the item being deleted, e.g. the sheet id */
  target?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ visible, loading = false, target, onCancel, onConfirm }) => {
  const handleHide = () => {
    if (!loading) onCancel();
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: 'linear-gradient(135deg,#c62828,#e53935)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(198,40,40,0.28)',
        }}
      >
        <Trash2 size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>
          Delete this sync?
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          {target ? (
            <>
              Sheet <strong style={{ color: C.teal, fontFamily: 'monospace' }}>{target}</strong>
            </>
          ) : (
            'This action cannot be undone'
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="delsync-dialog"
      maskClassName="delsync-mask"
      style={{ width: '90vw', maxWidth: '460px' }}
      modal
      closable={!loading}
      dismissableMask={!loading}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .delsync-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: delsyncMaskIn 0.3s ease; }
        .delsync-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: delsyncDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
        .delsync-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .delsync-dialog .p-dialog-header-icon { color: ${C.sub}; }
        .delsync-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
        @keyframes delsyncMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes delsyncDlgIn { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .delsync-del:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(198,40,40,0.35) !important; }
        .delsync-spin { animation: delsyncSpin 0.9s linear infinite; }
        @keyframes delsyncSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#374151', lineHeight: 1.6 }}>
              This removes the sync and all of its staged rows from your history.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '8px 10px',
                borderRadius: '8px',
                background: 'rgba(22,163,74,0.06)',
                border: '1px solid rgba(22,163,74,0.2)',
                fontSize: '0.7rem',
                color: '#14532d',
                lineHeight: 1.5,
              }}
            >
              <CircleCheck size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: '1px' }} />
              Leads that were already pushed to your database are not affected.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', marginTop: '2px' }}>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onCancel} disabled={loading} />
          <button
            onClick={onConfirm}
            disabled={loading}
            className="delsync-del"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '0 18px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg,#c62828,#e53935)',
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.3px',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.85 : 1,
              boxShadow: '0 4px 12px rgba(198,40,40,0.25)',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? <Loader2 size={14} className="delsync-spin" /> : <Trash2 size={13} />}
            {loading ? 'DELETING...' : 'DELETE'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmDeleteModal;
